<script setup>
import { computed, watch } from 'vue';
import { useStore } from 'vuex';
import UiTableCard from '@/components/ui/UiTableCard.vue';
import { apiRequest } from '@/api/http';
import { useListResource } from '@/composables/useListResource';
import { useOpenPdf } from '@/composables/useOpenPdf';
import { useSessionGuard } from '@/composables/useSessionGuard';
import UiScheduleCell from '@/components/ui/UiScheduleCell.vue';
import UiTermSelect from '@/components/ui/UiTermSelect.vue';
import { useTerm } from '@/composables/useTerm';
import { WEEKDAYS, formatTimeRange } from '@/utils/format';
import { schoolDayLabel, withSchoolDayRows } from '@/constants/schoolDay';

useSessionGuard();

const headers = [
  { title: 'Time', key: 'time', sortable: false },
  ...WEEKDAYS.map((day) => ({ title: day, key: day, sortable: false })),
];

const store = useStore();
const year = computed(() => store.getters.getFilterSelected);
// Each term has its own schedule.
const { hasTerms, terms, term, termQuery } = useTerm();
const { items, loading, error, load } = useListResource(() =>
  apiRequest('get', `/enroll-student/MySchedule/${year.value}?${termQuery.value}`),
);
watch([year, term], load, { immediate: true });
// Flag ceremony, breaks and lunch get their own rows between the classes.
const rows = computed(() => withSchoolDayRows(items.value));

const openPdf = useOpenPdf();
// No term = the whole year: a year graded in terms prints a page per term.
function print(only = null) {
  const query = only ? `?term=${encodeURIComponent(only)}` : '';
  openPdf(`/pdf-generator/getMySchedule/${store.state.user.id}/${year.value}${query}`, 'Could not create Form 29.');
}
</script>

<template>
  <UiTableCard
    title="My schedule"
    description="Your weekly classes for the selected school year."
    :headers="headers"
    :items="rows"
    :band-label="schoolDayLabel"
    :loading="loading"
    :error="error"
    :searchable="false"
    :items-per-page="-1"
    empty-text="No classes scheduled yet."
  >
    <template v-if="hasTerms" #toolbar>
      <UiTermSelect v-model="term" :items="terms" />
    </template>
    <template #actions>
      <v-menu v-if="hasTerms" location="bottom end" offset="4">
        <template #activator="{ props: menuProps }">
          <v-btn v-bind="menuProps" variant="outlined" prepend-icon="mdi-printer-outline" append-icon="mdi-chevron-down">
            Print
          </v-btn>
        </template>
        <v-list density="compact">
          <v-list-item title="All terms" @click="print()" />
          <v-list-item v-for="t in terms" :key="t" :title="`${t} only`" @click="print(t)" />
        </v-list>
      </v-menu>
      <v-btn v-else variant="outlined" prepend-icon="mdi-printer-outline" @click="print()">Print</v-btn>
    </template>
    <template #[`item.time`]="{ item }">
      <span class="font-weight-medium text-no-wrap">{{ formatTimeRange(item.time) }}</span>
    </template>
    <template v-for="day in WEEKDAYS" :key="day" #[`item.${day}`]="{ item }">
      <UiScheduleCell :value="item[day]" />
    </template>
  </UiTableCard>
</template>
